import React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import capitalize from 'lodash/capitalize';
import PropTypes from 'prop-types';
import AddRemoveControl from '@/components/common/AddRemoveControl';
import ControlledPhoneInput from '@/components/common/form/ControlledPhoneInput';
import ControlledSelect from '@/components/common/form/ControlledSelect';
import PhoneLabels from '@/constants/ui/PhoneLabels';
import WorkPhoneLabels from '@/constants/ui/WorkPhoneLabels';
import translate from '@/lib/intl/translate';
import { ConstituentType } from '@/lib/types/ConstituentPropType';
import ConstituentField from '../constants/ConstituentField';
import LockedFormHelperTextProps from '../constants/LockedFormHelperTextProps';
import LockedSalesforceHelperText from './LockedSalesforceHelperText';
import PrimarySetterButton from './PrimarySetterButton';

const getNewPhone = (primary = false) => ({
  country: 'us',
  label: '',
  number: '',
  primary,
});

function PhoneFields({ locked }) {
  const { control, setValue, watch } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: ConstituentField.PHONES,
  });
  const constituentType = watch(ConstituentField.CONSTITUENT_TYPE);
  const options =
    constituentType === ConstituentType.ORG ? WorkPhoneLabels : PhoneLabels;

  React.useEffect(() => {
    if (!fields.length) {
      append(getNewPhone(true));
    }
  }, [fields]);

  return (
    <>
      {fields.map((item, i, arr) => (
        <React.Fragment key={item.id}>
          <FormControl className="relative lg:col-span-3" disabled={locked}>
            <ControlledPhoneInput
              control={control}
              defaultValue={item.number}
              disabled={locked}
              fullWidth
              label={translate('PHONE_NUMBERS_LABEL')}
              name={`${ConstituentField.PHONES}.${i}.number`}
              onCountryChange={(country) =>
                setValue(`${ConstituentField.PHONES}.${i}.country`, country)
              }
            />
            {arr.length > 1 && (
              <PrimarySetterButton
                className="absolute right-2 top-1.5"
                disabled={locked}
                index={i}
                name={ConstituentField.PHONES}
              />
            )}

            {locked && (
              <FormHelperText {...LockedFormHelperTextProps}>
                <LockedSalesforceHelperText locked />
              </FormHelperText>
            )}
          </FormControl>

          <div className="relative flex items-center">
            <FormControl className="flex-grow lg:w-full" disabled={locked}>
              <InputLabel>{translate('TYPE_LABEL')}</InputLabel>
              <ControlledSelect
                control={control}
                disabled={locked}
                name={`${ConstituentField.PHONES}.${i}.label`}
                label={translate('TYPE_LABEL')}
              >
                {options.map((val) => (
                  <MenuItem key={val} value={val}>
                    {capitalize(val)}
                  </MenuItem>
                ))}
              </ControlledSelect>

              {locked && (
                <FormHelperText {...LockedFormHelperTextProps}>
                  <LockedSalesforceHelperText locked />
                </FormHelperText>
              )}
            </FormControl>

            <AddRemoveControl
              className="flex-shrink-0 pl-4 lg:absolute lg:-right-16 lg:-mr-2"
              disabled={locked}
              onAdd={() => append(getNewPhone())}
              onRemove={() => remove(i)}
              showAdd={i === arr.length - 1}
              showRemove={arr.length > 1}
            />
          </div>
        </React.Fragment>
      ))}
    </>
  );
}

PhoneFields.propTypes = {
  locked: PropTypes.bool.isRequired,
};

export default PhoneFields;
